import type { UiPoint } from '@streamlain/copilot-realtime-contract';

/**
 * The user's own input, dispatched by the app for the AI: pointer gestures at
 * points and text typed at the caret. No app API is called; whatever takes the
 * user's input takes this.
 */

/**
 * A document's window with its own classes, which its elements and events are
 * instances of.
 */
type View = Window & typeof globalThis;

export const nextFrame = (view: Window) =>
  new Promise<void>(resolve => view.requestAnimationFrame(() => resolve()));

/** The innermost element drawn at a viewport point, through open shadow roots. */
export function hitAt(document: Document, point: UiPoint): Element | null {
  let hit = document.elementFromPoint(point.x, point.y);
  while (hit?.shadowRoot) {
    const inner = hit.shadowRoot.elementFromPoint(point.x, point.y);
    if (!inner || inner === hit) break;
    hit = inner;
  }
  return hit;
}

/** The node around a node, out of shadow roots too. */
export const around = (node: Node): Node | null => {
  const parent = node.parentNode;
  return parent instanceof ShadowRoot ? parent.host : parent;
};

export const contains = (root: Element, node: Element) => {
  for (let current: Node | null = node; current; current = around(current))
    if (current === root) return true;
  return false;
};

/** The outermost editable around a region: what a click into the text focuses. */
function editingHost(region: HTMLElement): HTMLElement {
  let host = region;
  for (
    let parent = host.parentElement;
    parent?.isContentEditable;
    parent = parent.parentElement
  )
    host = parent;
  return host;
}

/**
 * What a press does by itself: the focus goes to what was pressed, and leaves
 * what had it when the pressed place takes none. Editors close and save on it.
 */
function focusPressed(target: Element) {
  const document = target.ownerDocument;
  const view = document.defaultView!;
  let active = document.activeElement;
  while (active?.shadowRoot?.activeElement)
    active = active.shadowRoot.activeElement;
  let taker: HTMLElement | null = null;
  for (let node: Node | null = target; node && !taker; node = around(node)) {
    if (!(node instanceof view.HTMLElement)) continue;
    if (node.isContentEditable) taker = editingHost(node);
    else if (node.tabIndex >= 0 || node.hasAttribute('tabindex')) taker = node;
  }
  if (taker === active) return;
  if (taker) taker.focus({ preventScroll: true });
  else if (active instanceof view.HTMLElement && active !== document.body)
    active.blur();
}

/** Where the pointer goes down and, for a drag, where it is released. */
export interface Gesture {
  at: UiPoint;
  /** Receives the pointer at `at`: the element itself or what is drawn there. */
  start: Element;
  to?: UiPoint;
}

const MOUSE_POINTER_ID = 1;

/**
 * What the browser drags itself when the pointer moves off with it: the nearest
 * element around the pressed one that asks for it. Such an element is carried
 * with drag events, and its place of release gets a drop.
 */
function draggedBy(pressed: Element, view: View): HTMLElement | null {
  for (let node: Node | null = pressed; node; node = around(node))
    if (
      node instanceof view.HTMLElement &&
      node.getAttribute('draggable') === 'true'
    )
      return node;
  return null;
}

const PRIMARY_BUTTON = 0;
const SECONDARY_BUTTON = 2;
/** The `buttons` bit of the secondary button while it is down. */
const SECONDARY_BUTTON_DOWN = 2;

/**
 * The most one packet of a wheel gesture carries, in pixels. A swipe on a touch
 * pad arrives in packets this small; one large turn is a notch of a mouse
 * wheel, which a canvas zooms on instead of moving its view.
 */
const WHEEL_PACKET = 24;

/** What the wheel scrolls when no one takes it: the nearest content around that can move that way. */
function scrollerOf(start: Element, by: UiPoint, view: View): Element | null {
  const moves = (overflow: string) =>
    /^(?:auto|scroll|overlay)$/u.test(overflow);
  for (let node: Node | null = start; node; node = around(node)) {
    if (!(node instanceof view.HTMLElement)) continue;
    const style = view.getComputedStyle(node);
    if (
      (by.y !== 0 &&
        node.scrollHeight > node.clientHeight &&
        moves(style.overflowY)) ||
      (by.x !== 0 &&
        node.scrollWidth > node.clientWidth &&
        moves(style.overflowX))
    )
      return node;
  }
  return start.ownerDocument.scrollingElement;
}

/**
 * One pointer gesture in the user's own steps: press, move, let go. Moving off
 * with an element the browser drags itself carries it the browser's way: drag
 * events instead of pointer moves, and a drop where it is let go.
 */
export function pointerOf(gesture: Gesture) {
  const document = gesture.start.ownerDocument;
  const view = document.defaultView!;
  const init = (
    point: UiPoint,
    buttons: number,
    button = PRIMARY_BUTTON,
    bubbles = true
  ) => ({
    bubbles,
    cancelable: true,
    composed: true,
    button,
    buttons,
    clientX: point.x,
    clientY: point.y,
  });
  /** Whether the pointer event and the mouse event that follows it were left alone. */
  const pair = (
    target: Element,
    type: string,
    point: UiPoint,
    buttons: number,
    button = PRIMARY_BUTTON,
    bubbles = true
  ) => ({
    pointer: target.dispatchEvent(
      new view.PointerEvent(`pointer${type}`, {
        ...init(point, buttons, button, bubbles),
        pointerId: MOUSE_POINTER_ID,
        pointerType: 'mouse',
        isPrimary: true,
      })
    ),
    mouse: target.dispatchEvent(
      new view.MouseEvent(`mouse${type}`, init(point, buttons, button, bubbles))
    ),
  });
  /** False when what took the mouse event wants none of what follows from it. */
  const send = (target: Element, type: string, point: UiPoint, buttons = 1) =>
    pair(target, type, point, buttons).mouse;
  let last = gesture.start,
    point = gesture.at;
  let dragged: HTMLElement | null = null;
  /** What the dragged element carries, from the moment the browser's drag has started. */
  let carried: DataTransfer | null = null;
  let offered = false;
  /** What the dragged element is over, and whether it takes a drop. */
  let over: Element | null = null,
    taken = false;
  const drag = (target: Element, type: string, buttons = 1) => {
    const event = new view.DragEvent(type, {
      ...init(point, buttons),
      dataTransfer: carried,
    });
    target.dispatchEvent(event);
    return event;
  };
  /** The first move with a draggable element starts the browser's drag, unless the app refuses it. */
  const carry = () => {
    if (!dragged || offered) return;
    offered = true;
    carried = new view.DataTransfer();
    if (drag(dragged, 'dragstart').defaultPrevented) {
      carried = null;
      return;
    }
    // The pointer is the drag's from here on.
    last.dispatchEvent(
      new view.PointerEvent('pointercancel', {
        bubbles: true,
        composed: true,
        pointerId: MOUSE_POINTER_ID,
        pointerType: 'mouse',
        isPrimary: true,
      })
    );
  };
  /** The pointer is where the gesture happens before anything is pressed. */
  const arrive = () => {
    send(last, 'move', point, 0);
    // What the pointer's arrival took away is not there: the gesture goes to what is there now.
    if (!last.isConnected) last = hitAt(document, point) ?? last;
  };
  return {
    /**
     * The pointer is at the point before it goes down: what is drawn there knows it is under
     * the pointer, like under the user's. False when what was pressed took the press for
     * itself, so the browser does nothing of its own with it (no text selection).
     */
    press: () => {
      arrive();
      dragged = draggedBy(last, view);
      const down = pair(last, 'down', point, 1);
      if (down.mouse) focusPressed(last);
      return down.pointer && down.mouse;
    },
    /**
     * A right click: the secondary button goes down and up, and the browser asks for the
     * context menu of what is there.
     */
    context: () => {
      arrive();
      pair(last, 'down', point, SECONDARY_BUTTON_DOWN, SECONDARY_BUTTON);
      pair(last, 'up', point, 0, SECONDARY_BUTTON);
      last.dispatchEvent(
        new view.MouseEvent('contextmenu', init(point, 0, SECONDARY_BUTTON))
      );
    },
    /** The pointer comes to rest over what is drawn there; nothing is pressed. */
    hover: () => {
      pair(last, 'over', point, 0);
      pair(last, 'enter', point, 0, PRIMARY_BUTTON, false);
      arrive();
    },
    /**
     * The wheel turns over what is drawn there, as a swipe on a touch pad: packet by packet.
     * What takes a packet for itself (a canvas moving its view) does the rest; the content
     * around it scrolls by what nothing took, as it would for the user.
     */
    wheel: (by: UiPoint) => {
      arrive();
      const packets = Math.max(
        1,
        Math.ceil(Math.max(Math.abs(by.x), Math.abs(by.y)) / WHEEL_PACKET)
      );
      let free = 0;
      for (let sent = 0; sent < packets; sent++)
        if (
          last.dispatchEvent(
            new view.WheelEvent('wheel', {
              ...init(point, 0),
              deltaX: by.x / packets,
              deltaY: by.y / packets,
              deltaMode: 0,
            })
          )
        )
          free++;
      if (free)
        scrollerOf(last, by, view)?.scrollBy({
          left: (by.x * free) / packets,
          top: (by.y * free) / packets,
          behavior: 'instant',
        });
    },
    /** The pointer goes to what is drawn under it now, like the user's move. */
    move: (to: UiPoint) => {
      carry();
      point = to;
      last = hitAt(document, to) ?? last;
      if (!carried) {
        send(last, 'move', to);
        return;
      }
      if (last !== over) {
        const left = over;
        over = last;
        drag(over, 'dragenter');
        if (left) drag(left, 'dragleave');
      }
      // A place takes a drop by refusing the default, which is to take none.
      taken = drag(over!, 'dragover').defaultPrevented;
    },
    /** `second` lets go of the second press of a double click. */
    release: (click: boolean, second = false) => {
      if (carried && dragged) {
        if (over) drag(over, taken ? 'drop' : 'dragleave', 0);
        drag(dragged, 'dragend', 0);
        return;
      }
      send(last, 'up', point, 0);
      if (!click) return;
      const clicked = { ...init(point, 0), ...(second ? { detail: 2 } : {}) };
      last.dispatchEvent(new view.MouseEvent('click', clicked));
      if (second) last.dispatchEvent(new view.MouseEvent('dblclick', clicked));
    },
  };
}

/** A double click: two presses at one point, the second one counted as such. */
export function doubleClick(hand: ReturnType<typeof pointerOf>) {
  hand.press();
  hand.release(true);
  hand.press();
  hand.release(true, true);
}

/**
 * A place in existing text, named by a stretch of it that is there exactly
 * once: the caret goes behind the stretch, or the typed text takes its place
 * (no text takes it away).
 */
export type TextAnchor = { after: string } | { replace: string };

/**
 * Where an anchor puts typed text in a plain value: over the stretch it
 * replaces, or behind the stretch it follows. Null when that stretch is not in
 * the value exactly once.
 */
export function placeOf(
  value: string,
  anchor: TextAnchor
): { start: number; end: number } | null {
  const replaced = 'replace' in anchor;
  const wanted = replaced ? anchor.replace : anchor.after;
  const at = wanted ? value.indexOf(wanted) : -1;
  if (at < 0 || value.includes(wanted, at + 1)) return null;
  const end = at + wanted.length;
  return { start: replaced ? at : end, end };
}

/**
 * A text node the browser shows as text of its own: whitespace between the
 * elements of a template is none, even where whitespace is kept.
 */
function shown(node: Node, view: Window): boolean {
  const text = node.textContent ?? '';
  if (/\S/u.test(text)) return true;
  const parent = node.parentElement;
  return (
    text.length > 0 &&
    !!parent &&
    !parent.firstElementChild &&
    !/^(?:normal|nowrap)$/u.test(view.getComputedStyle(parent).whiteSpace)
  );
}

/**
 * The stretch of shown text that reads `snippet`, or null unless exactly one
 * of the regions shows it exactly once: text is never changed at a guessed place.
 * Read `spaced`, the stretch is found by its characters alone: a view that draws
 * its text piece by piece (the lines of a PDF page) keeps no reliable spaces.
 */
export function stretchOf(
  regions: readonly HTMLElement[],
  snippet: string,
  view: View,
  spaced = true
): Range | null {
  const wanted = spaced ? snippet : snippet.replace(/\s+/gu, '');
  if (!wanted) return null;
  let found: Range | null = null;
  for (const region of regions) {
    const document = region.ownerDocument;
    /** Every character that counts, with the place it has in its text node. */
    const places: { node: Node; offset: number }[] = [];
    let text = '';
    const walker = document.createTreeWalker(region, view.NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      if (!shown(node, view)) continue;
      const content = node.textContent ?? '';
      for (let offset = 0; offset < content.length; offset++) {
        if (!spaced && /\s/u.test(content[offset])) continue;
        places.push({ node, offset });
        text += content[offset];
      }
    }
    for (
      let start = text.indexOf(wanted);
      start >= 0;
      start = text.indexOf(wanted, start + 1)
    ) {
      if (found) return null;
      // The stretch starts at its first character and ends behind its last.
      const first = places[start],
        last = places[start + wanted.length - 1];
      found = document.createRange();
      found.setStart(first.node, first.offset);
      found.setEnd(last.node, last.offset + 1);
    }
  }
  return found;
}
